import { useEffect } from 'react';
import { Control, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { OptionRowsSkeleton } from '@/components/core';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { UIPhoneInput } from '@/components/core/phone-input/phone-input';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui-kit/form';
import { FieldLabel } from '../required-fields';
import { BLOOD_GROUPS } from '@/constant/hr/blood-groups';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { ContactValues } from '../../../schemas/employee-form-schema';
import { CONTACT_REFERENCE_COUNT, MAX_HOBBIES } from '../../../schemas/contact-info-schema';
import { useGetContactCantons } from '../../../hooks/use-contact-info-lookups';
import { CantonOption } from '../../../services/contact-info-lookups.service';
import { HobbiesInput } from '../basic-info/hobbies-input';
import { FIELD_GRID, FormSection } from '../form-layout';

type FormValues = ContactValues;

type ReferenceField = 'Name' | 'Designation' | 'Company' | 'Email';

type TextFieldName =
  | 'Email'
  | 'Country'
  | 'PermanentCountry'
  | 'PrivateEmail'
  | 'EmergencyContactName'
  | 'EmergencyContactRelationship'
  | 'City'
  | 'ZipCode'
  | 'Address'
  | 'PermanentCity'
  | 'PermanentZipCode'
  | 'PermanentAddress'
  | 'LinkedInProfile'
  | 'GithubProfile'
  | 'FaceBookProfile'
  | 'WhatsAppProfile'
  | 'InstagramProfile'
  | 'ProfessionalImageUrl'
  | 'FathersName'
  | 'MothersName'
  | 'SpouseName'
  | 'PassportNo'
  | 'Nid'
  | 'Etin'
  | 'LatestAcademicTitle'
  | 'GraduationInstitute'
  | 'PostGraduationInstitute'
  | 'PreviousExperience'
  | 'PreviousOrganization'
  | `References.${number}.${ReferenceField}`;

type PhoneFieldName =
  | 'PhoneNumber'
  | 'PrivatePhoneNumber'
  | 'EmergencyContactPhoneNumber'
  | `References.${number}.Phone`;

type CheckboxFieldName = 'IsPrivateInfoVisible' | 'IsSameAsPresentAddress';

type SelectFieldName = 'Canton' | 'PermanentCanton' | 'BloodGroup';

interface SelectOption {
  value: string;
  label: string;
}

/** Keeps a stored value selectable even when it is not (or not yet) part of the loaded options. */
const withCurrentValue = (options: SelectOption[], current?: string): SelectOption[] =>
  current && !options.some((option) => option.value === current)
    ? [...options, { value: current, label: current }]
    : options;

interface TextFieldProps {
  control: Control<FormValues>;
  name: TextFieldName;
  label: string;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  className?: string;
}

const TextField = ({
  control,
  name,
  label,
  placeholder,
  type,
  disabled,
  className,
}: TextFieldProps) => {
  const { t } = useTranslation();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FieldLabel>{t(label)}</FieldLabel>
          <FormControl>
            <Input
              type={type}
              placeholder={placeholder ? t(placeholder) : undefined}
              {...field}
              value={field.value ?? ''}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

interface DateFieldProps {
  control: Control<FormValues>;
  name: 'PassportExpiryDate';
  label: string;
}

const DateField = ({ control, name, label }: DateFieldProps) => {
  const { t } = useTranslation();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FieldLabel>{t(label)}</FieldLabel>
          <FormControl>
            <DatePicker
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
              className="w-full"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

interface PhoneFieldProps {
  control: Control<FormValues>;
  name: PhoneFieldName;
  label: string;
  className?: string;
}

const PhoneField = ({ control, name, label, className }: PhoneFieldProps) => {
  const { t } = useTranslation();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FieldLabel>{t(label)}</FieldLabel>
          <FormControl>
            <UIPhoneInput
              {...field}
              onChange={(value?: string) => field.onChange(value ?? '')}
              placeholder={t(label)}
              international
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

interface CheckboxFieldProps {
  control: Control<FormValues>;
  name: CheckboxFieldName;
  label: string;
}

const CheckboxField = ({ control, name, label }: CheckboxFieldProps) => {
  const { t } = useTranslation();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={field.value}
              onCheckedChange={(checked) => field.onChange(checked === true)}
            />
            {t(label)}
          </label>
        </FormItem>
      )}
    />
  );
};

interface SelectFieldProps {
  control: Control<FormValues>;
  name: SelectFieldName;
  label: string;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  /** The options come from the API and are still loading. */
  isLoading?: boolean;
}

const SelectField = ({
  control,
  name,
  label,
  options,
  placeholder,
  disabled,
  isLoading = false,
}: SelectFieldProps) => {
  const { t } = useTranslation();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FieldLabel>{t(label)}</FieldLabel>
          <Select
            onValueChange={field.onChange}
            value={field.value || undefined}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger aria-label={t(label)}>
                <SelectValue placeholder={t(placeholder ?? label)} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {isLoading && <OptionRowsSkeleton />}
              {withCurrentValue(options, field.value).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

/** Mirrors the canton's LocationId (legacy CantonId / PermanentCantonId) from the selected canton name. */
const useCantonIdSync = (
  idField: 'CantonId' | 'PermanentCantonId',
  cantonName: string | undefined,
  cantons: CantonOption[] | undefined,
  enabled: boolean
) => {
  const { getValues, setValue } = useFormContext<FormValues>();

  useEffect(() => {
    if (!enabled || !cantons) return;
    const cantonId = cantons.find((canton) => canton.State === cantonName)?.ItemId ?? '';
    if ((getValues(idField) ?? '') !== cantonId) setValue(idField, cantonId);
  }, [enabled, cantons, cantonName, idField, getValues, setValue]);
};

export interface ContactInfoStepProps {
  /** A person editing their own profile: the business email (their login) stays read-only. */
  isSelfService?: boolean;
  /** The person must provide their extended profile (`hrm::people::require-extended-info`), so the personal and academic details are shown to them. */
  requiresExtendedInfo?: boolean;
}

/**
 * "Contact info" step, everything kept on the `UserContactDetail` (plus the business email / phone):
 * contacts, personal details, emergency contact, addresses, academic information, previous employment,
 * references and social profiles.
 */
export const ContactInfoStep = ({
  isSelfService = false,
  requiresExtendedInfo = false,
}: ContactInfoStepProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<FormValues>();

  const country = useWatch({ control, name: 'Country' });
  const canton = useWatch({ control, name: 'Canton' });
  const [address, city, zipCode, cantonId] = useWatch({
    control,
    name: ['Address', 'City', 'ZipCode', 'CantonId'],
  });
  const isSameAsPresent = useWatch({ control, name: 'IsSameAsPresentAddress' });
  const permanentCanton = useWatch({ control, name: 'PermanentCanton' });

  // Which sections show is decided by the viewer's permissions in the active organization, never by a
  // country name — replaces the legacy Germany/Switzerland/Bangladesh/Bhutan employment-location rules.
  const canViewPersonalInfo = useHasPermission(HRM_PERMISSIONS.PEOPLE.VIEW_PERSONAL_INFO);
  const showPersonalDetails = canViewPersonalInfo || requiresExtendedInfo;
  const showPermanentAddress = useHasPermission(HRM_PERMISSIONS.PEOPLE.CONTACT_PERMANENT_ADDRESS);
  const showReferences = useHasPermission(HRM_PERMISSIONS.PEOPLE.CONTACT_REFERENCES);
  const showSocialProfiles = useHasPermission(HRM_PERMISSIONS.PEOPLE.CONTACT_SOCIAL_PROFILES);
  const showGithubProfile = useHasPermission(HRM_PERMISSIONS.PEOPLE.CONTACT_GITHUB_PROFILE);
  const showMessengerProfiles = useHasPermission(
    HRM_PERMISSIONS.PEOPLE.CONTACT_MESSENGER_PROFILES
  );

  // The organization stands for the country, so both addresses pick from its cantons.
  const { data: cantons, isLoading: isCantonsLoading } = useGetContactCantons();
  const cantonOptions: SelectOption[] = (cantons ?? []).map((item) => ({
    value: item.State,
    label: item.State,
  }));

  useCantonIdSync('CantonId', canton, cantons, true);
  useCantonIdSync('PermanentCantonId', permanentCanton, cantons, !isSameAsPresent);

  // "Same as present": permanent fields mirror the present address (and stay read-only) while ticked.
  useEffect(() => {
    if (!isSameAsPresent) return;
    const options = { shouldDirty: true };
    setValue('PermanentCountry', country ?? '', options);
    setValue('PermanentCanton', canton ?? '', options);
    setValue('PermanentCantonId', cantonId ?? '', options);
    setValue('PermanentCity', city ?? '', options);
    setValue('PermanentZipCode', zipCode ?? '', options);
    setValue('PermanentAddress', address ?? '', options);
  }, [isSameAsPresent, country, canton, cantonId, city, zipCode, address, setValue]);

  const referenceIndexes = Array.from({ length: CONTACT_REFERENCE_COUNT }, (_, index) => index);

  return (
    <div className="flex flex-col gap-8">
      <FormSection title={t('BUSINESS')}>
        <div className={FIELD_GRID}>
          <TextField
            control={control}
            name="Email"
            label="EMAIL"
            placeholder="TYPE_EMAIL"
            type="email"
            disabled={isSelfService}
          />
          <PhoneField control={control} name="PhoneNumber" label="TELEPHONE_NO" />
        </div>
      </FormSection>

      <FormSection
        title={t('PRIVATE')}
        action={
          <CheckboxField control={control} name="IsPrivateInfoVisible" label="SHOW_ON_DIRECTORY" />
        }
      >
        <div className={FIELD_GRID}>
          <TextField
            control={control}
            name="PrivateEmail"
            label="EMAIL"
            placeholder="TYPE_EMAIL"
            type="email"
          />
          <PhoneField control={control} name="PrivatePhoneNumber" label="TELEPHONE_NO" />
        </div>
      </FormSection>

      {showPersonalDetails && (
        <FormSection title={t('PERSONAL_DETAILS')}>
          <div className={FIELD_GRID}>
            <TextField
              control={control}
              name="FathersName"
              label="FATHERS_NAME"
              placeholder="TYPE_NAME"
            />
            <TextField
              control={control}
              name="MothersName"
              label="MOTHERS_NAME"
              placeholder="TYPE_NAME"
            />
            <TextField
              control={control}
              name="SpouseName"
              label="SPOUSE_NAME"
              placeholder="TYPE_NAME"
            />
            <SelectField
              control={control}
              name="BloodGroup"
              label="BLOOD_GROUP"
              placeholder="SELECT_GROUP"
              options={BLOOD_GROUPS.map((group) => ({ value: group, label: group }))}
            />
            <TextField
              control={control}
              name="PassportNo"
              label="PASSPORT_NO"
              placeholder="TYPE_PASSPORT_NUMBER"
            />
            <DateField control={control} name="PassportExpiryDate" label="PASSPORT_EXPIRY_DATE" />
            <TextField control={control} name="Nid" label="NID" placeholder="TYPE_YOUR_NID" />
            <TextField control={control} name="Etin" label="E_TIN" placeholder="TYPE_YOUR_ETIN" />
          </div>
          <FormField
            control={control}
            name="HobbiesInterests"
            render={({ field }) => (
              <FormItem>
                <FieldLabel>{t('HOBBIES_INTERESTS')}</FieldLabel>
                <FormControl>
                  <HobbiesInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('ADD_HOBBY')}
                    max={MAX_HOBBIES}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>
      )}

      <FormSection title={t('EMERGENCY_CONTACT')}>
        <div className={FIELD_GRID}>
          <TextField
            control={control}
            name="EmergencyContactName"
            label="EMERGENCY_CONTACT_NAME"
            placeholder="TYPE_NAME"
          />
          <PhoneField control={control} name="EmergencyContactPhoneNumber" label="TELEPHONE_NO" />
          <TextField
            control={control}
            name="EmergencyContactRelationship"
            label="EMERGENCY_CONTACT_RELATIONSHIP"
            placeholder="TYPE_RELATIONSHIP"
          />
        </div>
      </FormSection>

      <FormSection title={t('PRESENT_ADDRESS')}>
        <div className={FIELD_GRID}>
          <TextField control={control} name="Country" label="COUNTRY" placeholder="COUNTRY" />
          <SelectField
            control={control}
            name="Canton"
            label="CANTON_DIVISION_DISTRICT_STATE"
            options={cantonOptions}
            isLoading={isCantonsLoading}
          />
          <TextField control={control} name="City" label="CITY" placeholder="TYPE_CITY" />
          <TextField
            control={control}
            name="ZipCode"
            label="ZIP_CODE"
            placeholder="TYPE_ZIP_CODE"
          />
          <TextField
            control={control}
            name="Address"
            label="STREET_ADDRESS"
            placeholder="TYPE_STREET_ADDRESS"
            className="sm:col-span-2"
          />
        </div>
      </FormSection>

      {showPermanentAddress && (
        <FormSection
          title={t('PERMANENT_ADDRESS')}
          action={
            <CheckboxField
              control={control}
              name="IsSameAsPresentAddress"
              label="SAME_AS_PRESENT"
            />
          }
        >
          <div className={FIELD_GRID}>
            <TextField
              control={control}
              name="PermanentCountry"
              label="COUNTRY"
              placeholder="COUNTRY"
              disabled={isSameAsPresent}
            />
            <SelectField
              control={control}
              name="PermanentCanton"
              label="CANTON_DIVISION_DISTRICT_STATE"
              options={cantonOptions}
              isLoading={isCantonsLoading}
              disabled={isSameAsPresent}
            />
            <TextField
              control={control}
              name="PermanentCity"
              label="CITY"
              placeholder="TYPE_CITY"
              disabled={isSameAsPresent}
            />
            <TextField
              control={control}
              name="PermanentZipCode"
              label="ZIP_CODE"
              placeholder="TYPE_ZIP_CODE"
              disabled={isSameAsPresent}
            />
            <TextField
              control={control}
              name="PermanentAddress"
              label="STREET_ADDRESS"
              placeholder="TYPE_STREET_ADDRESS"
              disabled={isSameAsPresent}
              className="sm:col-span-2"
            />
          </div>
        </FormSection>
      )}

      {showPersonalDetails && (
        <>
          <FormSection title={t('ACADEMIC_INFORMATION')}>
            <div className={FIELD_GRID}>
              <TextField
                control={control}
                name="LatestAcademicTitle"
                label="LATEST_ACADEMIC_TITLE"
                placeholder="TYPE_LATEST_ACADEMIC_TITLE"
              />
              <TextField
                control={control}
                name="GraduationInstitute"
                label="GRADUATION_INSTITUTE"
                placeholder="TYPE_GRADUATION_INSTITUTE"
              />
              <TextField
                control={control}
                name="PostGraduationInstitute"
                label="POST_GRADUATION_INSTITUTE"
                placeholder="TYPE_POST_GRADUATION_INSTITUTE"
              />
            </div>
          </FormSection>

          <FormSection title={t('PREVIOUS_WORKING_INFORMATION')}>
            <div className={FIELD_GRID}>
              <TextField
                control={control}
                name="PreviousExperience"
                label="PREVIOUS_EXPERIENCE"
                placeholder="TYPE_PREVIOUS_EXPERIENCE"
              />
              <TextField
                control={control}
                name="PreviousOrganization"
                label="PREVIOUS_ORGANIZATION"
                placeholder="TYPE_PREVIOUS_ORGANIZATION"
              />
            </div>
          </FormSection>
        </>
      )}

      {showReferences && (
        <FormSection title={t('REFERENCE_DETAILS')}>
          {referenceIndexes.map((index) => (
            <div key={index} className="flex flex-col gap-4 rounded-lg border bg-muted/30 p-4">
              <h4 className="text-sm font-medium text-muted-foreground">
                {t('REFERENCE')} {String(index + 1).padStart(2, '0')}
              </h4>
              <div className={FIELD_GRID}>
                <TextField
                  control={control}
                  name={`References.${index}.Name`}
                  label="NAME"
                  placeholder="TYPE_NAME"
                />
                <TextField
                  control={control}
                  name={`References.${index}.Designation`}
                  label="DESIGNATION"
                  placeholder="TYPE_DESIGNATION"
                />
                <TextField
                  control={control}
                  name={`References.${index}.Company`}
                  label="COMPANY"
                  placeholder="TYPE_COMPANY"
                />
                <TextField
                  control={control}
                  name={`References.${index}.Email`}
                  label="EMAIL"
                  placeholder="TYPE_EMAIL"
                  type="email"
                />
                <PhoneField
                  control={control}
                  name={`References.${index}.Phone`}
                  label="TELEPHONE_NO"
                />
              </div>
            </div>
          ))}
        </FormSection>
      )}

      {showSocialProfiles && (
        <FormSection title={t('SOCIAL_PROFILES')}>
          <div className={FIELD_GRID}>
            <TextField
              control={control}
              name="LinkedInProfile"
              label="LINKEDIN_PROFILE"
              placeholder="TYPE_URL"
            />
            {showGithubProfile && (
              <TextField
                control={control}
                name="GithubProfile"
                label="GITHUB_PROFILE"
                placeholder="TYPE_URL"
              />
            )}
            {showMessengerProfiles && (
              <>
                <TextField
                  control={control}
                  name="FaceBookProfile"
                  label="FACEBOOK_PROFILE"
                  placeholder="TYPE_URL"
                />
                <TextField
                  control={control}
                  name="WhatsAppProfile"
                  label="WHATSAPP_PROFILE"
                  placeholder="TYPE_URL"
                />
                <TextField
                  control={control}
                  name="InstagramProfile"
                  label="INSTAGRAM_PROFILE"
                  placeholder="TYPE_URL"
                />
              </>
            )}
            <TextField
              control={control}
              name="ProfessionalImageUrl"
              label="PROFESSIONAL_IMAGE_URL"
              placeholder="TYPE_URL"
            />
          </div>
        </FormSection>
      )}
    </div>
  );
};
